import { useEffect, useState } from 'react';
import {
  createPromoCode,
  listPromoCodes,
  updatePromoCode,
  togglePromoCode,
  promoRedemptions,
  listPromotions,
  listCardOffers,
  disablePromotion,
  enablePromotion,
  uploadAdminCouponImage,
} from '../../api/adminDeals';
import { apiGet } from '../../services/apiClient';

const emptyForm = {
  code: '',
  coupon_type: 'CODE',
  card_image_url: '',
  scope: 'platform',
  host_id: '',
  property_ids: [],
  name: '',
  description: '',
  discount_type: 'percent',
  discount_value: 10,
  min_nights: 1,
  start_date: '',
  end_date: '',
  max_uses_total: '',
  max_uses_per_guest: '',
  first_time_only: false,
  is_active: true,
  image_url: '',
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const AdminCoupons = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [cardOffers, setCardOffers] = useState([]);
  const [properties, setProperties] = useState([]);
  const propertyMap = new Map(properties.map((p) => [String(p.id), p.title || `Property ${p.id}`]));

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [codes, promos, cards, props] = await Promise.all([
        listPromoCodes(),
        listPromotions(),
        listCardOffers(),
        apiGet('/api/admin/properties?limit=200'),
      ]);
      setItems(asArray(codes));
      setPromotions(asArray(promos));
      setCardOffers(asArray(cards));
      setProperties(asArray(props));
    } catch (err) {
      setError(err.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const propertyIds = form.scope === 'property' ? form.property_ids : [];
      if (form.scope === 'host' && !form.host_id) {
        setError('Host ID is required when scope is set to Host.');
        return;
      }
      if (form.scope === 'property' && !propertyIds.length) {
        setError('Select at least one property for property-scoped discounts.');
        return;
      }
      const payload = {
        ...form,
        property_ids: propertyIds,
        code: form.coupon_type === 'CODE' ? form.code.toUpperCase() : form.code,
      };
      const saved = editingId ? await updatePromoCode(editingId, payload) : await createPromoCode(payload);
      const next = items.filter((i) => i.id !== saved.id);
      setItems([saved, ...next]);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err) {
      setError(err.message || 'Failed to save promo code');
    }
  };

  const startEdit = (row) => {
    const propertyIds = row.property_ids?.length ? row.property_ids : row.property_id ? [row.property_id] : [];
    setEditingId(row.id);
    setForm({
      ...emptyForm,
      ...row,
      host_id: row.host_id || '',
      property_ids: propertyIds.map((id) => String(id)),
      coupon_type: row.coupon_type || 'CODE',
      card_image_url: row.card_image_url || row.image_url || '',
    });
  };

  const toggleProperty = (id) => {
    setForm((prev) => {
      const next = new Set(prev.property_ids || []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, property_ids: Array.from(next) };
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      const res = await uploadAdminCouponImage(file);
      const path = res?.path || res?.url;
      if (!path) throw new Error('Upload failed');
      setForm((f) => ({ ...f, card_image_url: path }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  const viewRedemptions = async (row) => {
    try {
      const data = await promoRedemptions(row.id);
      setRedemptions(asArray(data));
    } catch {
      setRedemptions([]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="admin-page-title text-2xl font-semibold text-slate-900">Promotions</h1>
          <p className="text-sm text-slate-600">Create promo code discounts and review host offers.</p>
        </div>
        {loading && <span className="text-xs text-slate-500">Loading...</span>}
      </div>

      {error && <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      <form onSubmit={handleSubmit} className="admin-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Code</span>
            <input
              required={form.coupon_type === 'CODE'}
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              placeholder="WELCOME10"
              disabled={form.coupon_type === 'CARD'}
            />
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Coupon type</span>
            <select
              value={form.coupon_type}
              onChange={(e) => setForm((f) => ({ ...f, coupon_type: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            >
              <option value="CODE">Code</option>
              <option value="CARD">Card (image)</option>
            </select>
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Scope</span>
            <select
              value={form.scope}
              onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            >
              <option value="platform">Platform</option>
              <option value="host">Host</option>
              <option value="property">Property</option>
            </select>
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Host ID (optional)</span>
            <input
              value={form.host_id}
              onChange={(e) => setForm((f) => ({ ...f, host_id: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              disabled={form.scope === 'platform'}
            />
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Properties</span>
            <div className="max-h-32 overflow-auto rounded-lg border border-slate-200 bg-white p-2">
              {form.scope !== 'property' && <div className="text-xs text-slate-500">Select "Property" scope to assign.</div>}
              {form.scope === 'property' && properties.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 py-1">
                  <input
                    type="checkbox"
                    checked={form.property_ids.includes(String(p.id))}
                    onChange={() => toggleProperty(String(p.id))}
                  />
                  <span>{p.title || `Property ${p.id}`}</span>
                </label>
              ))}
              {form.scope === 'property' && !properties.length && <div className="text-xs text-slate-500">No properties found.</div>}
            </div>
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Name</span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              placeholder="New Year offer"
            />
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Discount</span>
            <div className="flex gap-2">
              <select
                value={form.discount_type}
                onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="percent">Percent</option>
                <option value="fixed">Fixed</option>
              </select>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.discount_value}
                onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                className="w-24 rounded-lg border border-slate-200 px-3 py-2"
              />
            </div>
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Date range</span>
            <div className="flex gap-2">
              <input
                type="date"
                required
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
              <input
                type="date"
                required
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </div>
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Min nights</span>
            <input
              type="number"
              min="1"
              value={form.min_nights}
              onChange={(e) => setForm((f) => ({ ...f, min_nights: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Usage limits</span>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                placeholder="Total"
                value={form.max_uses_total}
                onChange={(e) => setForm((f) => ({ ...f, max_uses_total: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
              <input
                type="number"
                min="0"
                placeholder="Per guest"
                value={form.max_uses_per_guest}
                onChange={(e) => setForm((f) => ({ ...f, max_uses_per_guest: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </div>
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Coupon image (optional)</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            />
            {form.card_image_url && (
              <img src={form.card_image_url} alt="coupon" className="mt-2 h-12 rounded border border-slate-200 object-cover" />
            )}
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.first_time_only}
              onChange={(e) => setForm((f) => ({ ...f, first_time_only: e.target.checked }))}
            />
            First-time guests only
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
            Active
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button type="submit" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            {editingId ? 'Update code' : 'Create code'}
          </button>
          {editingId && (
            <button type="button" className="text-sm text-slate-600 underline" onClick={() => { setEditingId(null); setForm(emptyForm); }}>
              Cancel edit
            </button>
          )}
        </div>
      </form>

      <div className="admin-card rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-lg font-semibold text-slate-900">Discounts</p>
          <button onClick={load} className="text-xs text-emerald-700 underline">
            Refresh
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {items.length === 0 && <div className="p-4 text-sm text-slate-600">No discounts yet.</div>}
          {items.map((c) => (
            <div key={c.id} className="flex items-start justify-between px-4 py-3 hover:bg-slate-50">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900">{c.code}</p>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 capitalize">{c.scope}</span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                    {c.discount_type} {c.discount_value}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {c.start_date} - {c.end_date} - Min {c.min_nights || 1} nights
                </p>
                {c.description && <p className="text-sm text-slate-700">{c.description}</p>}
                {c.host_id && <p className="text-xs text-slate-500">Host: {c.host_id}</p>}
                {Array.isArray(c.property_ids) && c.property_ids.length > 0 && (
                  <p className="text-xs text-slate-500">
                    Properties: {c.property_ids.map((id) => propertyMap.get(String(id)) || `#${id}`).join(', ')}
                  </p>
                )}
                {(c.card_image_url || c.image_url) && (
                  <img
                    alt={c.code}
                    src={c.card_image_url || c.image_url}
                    className="h-10 w-16 rounded border border-slate-200 object-cover mt-1"
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={!!c.is_active}
                    onChange={(e) => togglePromoCode(c.id, e.target.checked).then(load)}
                  />
                  Active
                </label>
                <button type="button" className="text-xs text-emerald-700 underline" onClick={() => startEdit(c)}>
                  Edit
                </button>
                <button type="button" className="text-xs text-slate-700 underline" onClick={() => viewRedemptions(c)}>
                  Redemptions
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {redemptions.length > 0 && (
        <div className="admin-card rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-lg font-semibold text-slate-900">Recent redemptions</p>
            <button className="text-xs text-slate-600 underline" onClick={() => setRedemptions([])}>
              Hide
            </button>
          </div>
          <div className="divide-y divide-slate-100 text-sm">
            {redemptions.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-4 py-2">
                <div>
                  <p className="font-semibold text-slate-900">Booking {r.booking_code || r.booking_id}</p>
                  <p className="text-xs text-slate-500">{r.redeemed_at}</p>
                </div>
                <div className="text-slate-700 text-xs">
                  Guest: {r.guest_id || 'n/a'} {r.total_amount ? `- Amount ${r.total_amount}` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="admin-card rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-lg font-semibold text-slate-900">Host promotions oversight</p>
          <button onClick={load} className="text-xs text-emerald-700 underline">
            Refresh
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {promotions.length === 0 && <div className="p-4 text-sm text-slate-600">No promotions found.</div>}
          {promotions.map((p) => (
            <div key={p.id} className="flex items-start justify-between px-4 py-3 hover:bg-slate-50">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900">{p.name || `Promotion #${p.id}`}</p>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {p.host_name ? p.host_name : `Host ${p.host_id}`}
                  </span>
                  {p.property_title && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">{p.property_title}</span>}
                </div>
                <p className="text-xs text-slate-500">
                  {p.start_date} - {p.end_date} - {p.discount_type} {p.discount_value} - Stack {p.stack_rule}
                </p>
                <p className="text-xs text-slate-500">Status: {p.approval_status}</p>
              </div>
              <div className="flex items-center gap-2">
                {p.approval_status === 'admin_disabled' ? (
                  <button
                    type="button"
                    className="text-xs text-emerald-700 underline"
                    onClick={() => enablePromotion(p.id).then(load)}
                  >
                    Enable
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-red-600 underline"
                    onClick={() => disablePromotion(p.id).then(load)}
                  >
                    Disable
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-card rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-lg font-semibold text-slate-900">Card offers</p>
          <button onClick={load} className="text-xs text-emerald-700 underline">
            Refresh
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {cardOffers.length === 0 && <div className="p-4 text-sm text-slate-600">No card offers found.</div>}
          {cardOffers.map((offer) => (
            <div key={offer.id} className="flex items-start justify-between px-4 py-3 hover:bg-slate-50">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900">{offer.provider_name || `Offer #${offer.id}`}</p>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {offer.host_name ? offer.host_name : offer.host_id ? `Host ${offer.host_id}` : 'Platform'}
                  </span>
                  {offer.property_title && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                      {offer.property_title}
                    </span>
                  )}
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 capitalize">
                    {offer.card_type} {offer.discount_type} {offer.discount_value}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {offer.start_date} - {offer.end_date}
                  {offer.min_amount ? ` - Min ${offer.min_amount}` : ''}
                </p>
                {offer.terms && <p className="text-sm text-slate-700">{offer.terms}</p>}
              </div>
              <div className="text-xs text-slate-600">{offer.is_active ? 'Active' : 'Inactive'}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminCoupons;


